import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { Check, XCircle } from "@phosphor-icons/react";

/**
 * PendingCollectionVerifications — approver queue for every non-cash
 * reconciled collection whose payment line is `verification_status=pending`.
 * Verify → the line counts against the due (running due drops).
 * Reject → line stays on the sale for audit but does NOT reduce the due;
 * the reconciled receipt remains on record.
 */
export default function PendingCollectionVerifications({ branchId }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_pending: 0, row_count: 0 });
  const [exchRows, setExchRows] = useState([]);
  const [exchTotals, setExchTotals] = useState({ total_pending: 0, row_count: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // `${sale_id}-${idx}` currently being decided

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      const [dueResp, exchResp] = await Promise.all([
        api.get("/sales/collections/pending-verification", { params }),
        api.get("/accounts/exchange-receivables/pending-verifications", { params }),
      ]);
      setRows(dueResp.data.rows || []);
      setTotals({ total_pending: dueResp.data.total_pending || 0, row_count: dueResp.data.row_count || 0 });
      setExchRows(exchResp.data.rows || []);
      setExchTotals({ total_pending: exchResp.data.total_pending || 0, row_count: exchResp.data.row_count || 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = async (row, decision) => {
    let reason = "";
    if (decision === "reject") {
      reason = window.prompt("Reason for rejection (required):", "") || "";
      if (!reason.trim()) return;
    }
    setBusy(`${row.sale_id}-${row.line_index}`);
    try {
      await api.post(`/sales/${row.sale_id}/verify-collection/${row.line_index}`, {
        decision, reason,
      });
      toast.success(decision === "verify" ? "Verified — due updated" : "Rejected");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const decideExch = async (row, decision) => {
    let reason = "";
    if (decision === "reject") {
      reason = window.prompt("Reason for rejection (required):", "") || "";
      if (!reason.trim()) return;
    }
    setBusy(`exch-${row.sale_id}-${row.receipt_index}`);
    try {
      await api.post(`/accounts/exchange-receivables/${row.sale_id}/verify-receipt/${row.receipt_index}`, {
        decision, reason,
      });
      toast.success(decision === "verify" ? "Verified — receivable credited" : "Rejected");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="pending-verification-report" className="bg-canvas-card border border-canvas-line">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Non-Cash Collections — Pending Verification</div>
          <div className="text-sm">
            <b>{totals.row_count}</b> collections awaiting approval — total <b>{fmtMoney(totals.total_pending)}</b>
          </div>
        </div>
        <div className="text-[11px] text-ink-muted font-mono max-w-xs text-right">
          Non-cash payments only count against the customer&apos;s due once verified. Cash is auto-verified at collection.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Paid On", "Branch", "Chassis #", "Customer", "Phone", "Method", "Amount ₹", "Reference / UTR", "Receipt #", "Collected By", ""].map((h) => (
                <th key={h} className="text-left px-2 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">Queue is empty — every collection is verified ✓</td></tr>
            )}
            {rows.map((r) => {
              const key = `${r.sale_id}-${r.line_index}`;
              return (
                <tr key={key} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40" data-testid={`pv-row-${key}`}>
                  <td className="px-2 py-2 font-mono text-[11px]">{r.paid_on || "—"}</td>
                  <td className="px-2 py-2">{r.branch_name || "—"}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{r.chassis_no || "—"}</td>
                  <td className="px-2 py-2 font-semibold">{r.customer_name || "—"}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{r.customer_phone || "—"}</td>
                  <td className="px-2 py-2 font-mono text-[10px] uppercase">{r.method}</td>
                  <td className="px-2 py-2 font-mono tabular-nums text-right font-semibold">{fmtMoney(r.amount)}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{r.reference || <span className="text-destructive">— missing —</span>}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{r.receipt_no || "—"}</td>
                  <td className="px-2 py-2 font-mono text-[10px] text-ink-muted">{r.collected_by || "—"}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => decide(r, "verify")}
                        disabled={busy === key}
                        data-testid={`pv-verify-${key}`}
                        className="px-2 py-1 border border-[#00713a] text-[#00713a] text-[10px] font-mono uppercase tracking-wider hover:bg-[#00713a] hover:text-white flex items-center gap-1 disabled:opacity-40"
                      >
                        <Check size={10} weight="bold" /> Verify
                      </button>
                      <button
                        onClick={() => decide(r, "reject")}
                        disabled={busy === key}
                        data-testid={`pv-reject-${key}`}
                        className="px-2 py-1 border border-destructive text-destructive text-[10px] font-mono uppercase tracking-wider hover:bg-destructive hover:text-white flex items-center gap-1 disabled:opacity-40"
                      >
                        <XCircle size={10} weight="bold" /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Evaluator (exchange) receipts pending verification */}
      <div className="border-t-2 border-canvas-line mt-1 pt-0">
        <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Evaluator (Exchange) Receipts — Pending Verification</div>
            <div className="text-sm">
              <b>{exchTotals.row_count}</b> receipts awaiting approval — total <b>{fmtMoney(exchTotals.total_pending)}</b>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                {["Received On", "Branch", "Chassis #", "Customer", "Phone", "Evaluator", "Method", "Amount ₹", "Reference / UTR", "Receipt #", "Received By", ""].map((h) => (
                  <th key={h} className="text-left px-2 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>}
              {!loading && exchRows.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">No evaluator receipts pending verification ✓</td></tr>
              )}
              {exchRows.map((r) => {
                const key = `exch-${r.sale_id}-${r.receipt_index}`;
                return (
                  <tr key={key} className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40" data-testid={`pv-exch-row-${key}`}>
                    <td className="px-2 py-2 font-mono text-[11px]">{r.received_on || "—"}</td>
                    <td className="px-2 py-2">{r.branch_name || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[11px]">{r.chassis_no || "—"}</td>
                    <td className="px-2 py-2 font-semibold">{r.customer_name || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[11px]">{r.customer_phone || "—"}</td>
                    <td className="px-2 py-2">{r.evaluator_name || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[10px] uppercase">{r.method}</td>
                    <td className="px-2 py-2 font-mono tabular-nums text-right font-semibold">{fmtMoney(r.amount)}</td>
                    <td className="px-2 py-2 font-mono text-[11px]">{r.reference || <span className="text-destructive">— missing —</span>}</td>
                    <td className="px-2 py-2 font-mono text-[11px]">{r.receipt_no || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[10px] text-ink-muted">{r.received_by || "—"}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => decideExch(r, "verify")}
                          disabled={busy === key}
                          data-testid={`pv-exch-verify-${key}`}
                          className="px-2 py-1 border border-[#00713a] text-[#00713a] text-[10px] font-mono uppercase tracking-wider hover:bg-[#00713a] hover:text-white flex items-center gap-1 disabled:opacity-40"
                        >
                          <Check size={10} weight="bold" /> Verify
                        </button>
                        <button
                          onClick={() => decideExch(r, "reject")}
                          disabled={busy === key}
                          data-testid={`pv-exch-reject-${key}`}
                          className="px-2 py-1 border border-destructive text-destructive text-[10px] font-mono uppercase tracking-wider hover:bg-destructive hover:text-white flex items-center gap-1 disabled:opacity-40"
                        >
                          <XCircle size={10} weight="bold" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
